import styled, { css } from 'styled-components'

const isVisible = css`
  visibility: visible;
  opacity: 1;
`

const isInvisible = css`
  visibility: hidden;
  opacity: 0.001;
`

const transitionValue = '0.30s ease-in-out'

const visibilityTransition = css`
  transition: opacity ${transitionValue}, visibility ${transitionValue};
`

const ContentContainer = styled.div`
  ${visibilityTransition}
  ${isVisible}
`

const SpinnerContainer = styled.div`
  ${visibilityTransition}
  ${isInvisible}
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translate(-50%, -50%);
`

const swapVisibility = (props) => css`
  background: ${props.kind === 'primary' ? props.theme.color_secondary_dark : 'inherit'};
  pointer-events: none;

  ${ContentContainer} {
    ${isInvisible}
  }

  ${SpinnerContainer} {
    ${isVisible}
  }
`

const Button = styled.button`
  text-align: center;
  letter-spacing: normal;
  background-image: none;
  margin-bottom: 0;
  border-radius: 3px;
  box-sizing: border-box;
  border: 2px solid;
  line-height: normal;
  cursor: pointer;
  padding: 10px 16px 11px 16px;
  overflow: hidden;

  svg {
    fill: currentColor;
    width: 16px;
    height: 16px;
    line-height: 0;
    margin-right: 5px;
    position: relative;
    top: 2px;
  }

  /* Theme specified style rules */
  ${(props) =>
    css`
      font-family: ${props.theme.font_family};
      font-weight: ${props.theme.font_weight_bold};
      font-size: ${props.theme.font_size_base};
      background-color: ${props.theme.color_white};
      color: ${props.theme.color_secondary};
      border-color: ${props.theme.color_secondary};
      transition: background-color ${props.theme.base_transition}, color ${props.theme.base_transition},
        border-color ${props.theme.base_transition};
    `}

  /* Hover style rules when not disabled*/
  ${(props) =>
    !props.disabled &&
    css`
      &:hover,
      &:active {
        background-color: ${props.theme.color_white};
        border-color: ${props.theme.color_secondary_dark};
        color: ${props.theme.color_secondary_dark};
      }
    `}

  /* Primary button style rules */
  ${(props) =>
    props.kind === 'primary' &&
    !props.disabled &&
    css`
      padding: 12px 16px 13px 16px;
      border-width: 0;
      background-color: ${props.theme.color_secondary};
      color: ${props.theme.color_white};

      &:hover,
      &:active {
        background-color: ${props.theme.color_secondary_dark};
        color: ${props.theme.color_white};
      }
    `}
    

  /* iconOnly style rules */
  ${(props) =>
    props.iconOnly &&
    css`
      line-height: 0;
      padding: 12px 14px;

      svg {
        margin-right: 0;
        top: 0;
      }
    `}

  ${(props) =>
    props.iconOnly &&
    props.kind === 'primary' &&
    css`
      padding: 14px 16px;
    `}

  /* Disabled button style rules */
  ${(props) =>
    props.disabled &&
    css`
      cursor: not-allowed;
      border-color: ${props.theme.color_grey_dark};
      color: ${props.theme.color_grey_dark};
    `}
    

  /* Disabled primary button style rules */
  ${(props) =>
    props.disabled &&
    props.kind === 'primary' &&
    css`
      border-width: 0;
      color: ${props.theme.color_grey_dark};
      background-color: ${props.theme.color_grey_mid};
      padding: 12px 16px 13px 16px;
    `}

    /* Disabled primary button iconOnly style rules */
  ${(props) =>
    props.disabled &&
    props.kind === 'primary' &&
    props.iconOnly &&
    css`
      padding: 14px 16px;
    `}

  /* Full width button style rules */
  ${(props) =>
    props.full &&
    css`
      display: block;
      width: 100%;
    `}

  /* Small button style rules */
  ${(props) =>
    props.small &&
    css`
      border-width: 1px;
      padding: 9px 10px;
    `}

  /* Small primary button style rules */
  ${(props) =>
    props.small &&
    props.kind === 'primary' &&
    css`
      border-width: 0px;
    `}

  /* Is loading */
  /* Can be triggered with an isLoading class if React is not available on the client */
  ${(props) => props.isLoading && !props.disabled && swapVisibility(props)}
  &.isLoading {
    ${(props) => swapVisibility(props)}
  }

`

const LoadingContainer = styled.div`
  position: relative;
  overflow: hidden;
`

export { Button, ContentContainer, LoadingContainer, SpinnerContainer }
